/**
 * AdminQuestionnaireI18n — Super Admin coverage view for the questionnaire
 * translation cache (Investment Strategy + Psychometric, 3 levels x 5
 * non-English languages). Read-only: shows which of the 30 combos have
 * already been warmed by the background `warm_all_translations()` task on
 * boot so admins have peace of mind without digging into Mongo.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Languages, CheckCircle2, Clock } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";

const BANK_LABELS = {
    investment_strategy: "Investment Strategy",
    psychometric: "Psychometric Baseline",
};
const LEVEL_LABELS = { basic: "Basic", intermediate: "Intermediate", advanced: "Advanced" };

export default function AdminQuestionnaireI18n() {
    const [data, setData] = useState(null);

    async function load() {
        setData(null);
        try {
            const { data } = await api.get("/admin/questionnaire-i18n/coverage");
            setData(data);
        } catch {
            toast.error("Could not load translation coverage");
            setData({ rows: [] });
        }
    }
    useEffect(() => { load(); }, []);

    const byBankLevel = {};
    (data?.rows || []).forEach((r) => {
        const key = `${r.bank_id}:${r.level}`;
        (byBankLevel[key] = byBankLevel[key] || []).push(r);
    });

    return (
        <div className="space-y-6" data-testid="admin-questionnaire-i18n">
            <PageHeader
                icon={Languages}
                eyebrow="Admin Tools"
                title="Questionnaire Translation Coverage"
                subtitle="Investment Strategy + Psychometric Baseline are translated on-demand into 5 languages and cached forever. This shows which of the 30 combos are already warmed."
                testId="admin-questionnaire-i18n-header"
            />

            {data && (
                <div className="flex items-center gap-4 flex-wrap" data-testid="qi18n-summary">
                    <Stat label="Cached" value={data.cached_count} color="text-emerald-500" testId="qi18n-cached-count" />
                    <Stat label="Pending" value={data.pending_count} color="text-amber-500" testId="qi18n-pending-count" />
                    <Stat label="Coverage" value={`${data.coverage_pct}%`} color="text-primary" testId="qi18n-coverage-pct" />
                    <Button variant="outline" size="sm" onClick={load} data-testid="qi18n-refresh-btn">Refresh</Button>
                </div>
            )}

            {!data && (
                <div className="text-sm text-muted-foreground" data-testid="qi18n-loading">Loading coverage…</div>
            )}

            {data && Object.entries(byBankLevel).map(([key, rows]) => {
                const [bankId, level] = key.split(":");
                return (
                    <div key={key} className="border border-border rounded-lg bg-card overflow-hidden" data-testid={`qi18n-group-${key.replace(":", "-")}`}>
                        <div className="px-4 py-2.5 border-b border-border bg-secondary/30 text-sm font-semibold">
                            {BANK_LABELS[bankId] || bankId} — {LEVEL_LABELS[level] || level}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-border">
                            {rows.map((r) => (
                                <div
                                    key={r.language}
                                    className="p-3 flex flex-col items-center gap-1 text-center"
                                    data-testid={`qi18n-cell-${bankId}-${level}-${r.language}`}
                                >
                                    <span className="text-xs font-semibold">{r.language_label}</span>
                                    {r.cached ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-medium">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Cached
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-amber-500 font-medium">
                                            <Clock className="w-3.5 h-3.5" /> Pending
                                        </span>
                                    )}
                                    {r.cached_at && (
                                        <span className="text-[10px] text-muted-foreground">{new Date(r.cached_at).toLocaleString()}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Stat({ label, value, color, testId }) {
    return (
        <div className="flex items-center gap-2" data-testid={testId}>
            <span className={`text-2xl font-bold ${color}`}>{value}</span>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
        </div>
    );
}
